import {
  HUDDLE_HEARTBEAT_MS,
  HuddleStatus,
  IHuddle,
  IHuddleJoin,
  IHuddleSignal,
  IHuddleSignalDelivery,
  IHuddleSpeakerCheck,
} from '@common/interfaces/huddles'
import type ChannelsApi from '../../api/channels'
import { HuddleAudio, LOCAL_LEVEL_KEY } from './huddleAudio'
import { IHuddleMeshDeps } from './huddleMesh'
import { IHuddleAudioChunk } from './huddleRecorder'
import { HuddleSpeakerTracker } from './huddleSpeakers'

/** Enough for a full room's offers and ICE candidates while the join is in flight. */
const EARLY_SIGNAL_LIMIT = 200

/** Nothing the pill shows has changed: a heartbeat echoing the room back needs no new snapshot. */
const isSameRoom = (current: IHuddle | null, next: IHuddle): boolean =>
  !!current &&
  current.id === next.id &&
  current.status === next.status &&
  current.arcHostUserId === next.arcHostUserId &&
  current.messageId === next.messageId &&
  current.peers.length === next.peers.length &&
  current.peers.every(
    (peer, index) =>
      peer.peerId === next.peers[index].peerId && peer.isMuted === next.peers[index].isMuted,
  )

export enum HuddleSessionStatus {
  Connecting = 'connecting',
  Live = 'live',
  Ended = 'ended',
}

export interface IHuddleSnapshot {
  status: HuddleSessionStatus
  channelId: string
  huddle: IHuddle | null
  /** This tab's seat. */
  peerId: string | null
  isMuted: boolean
  /** Peers whose audio has connected, by peer id. */
  connectedPeerIds: string[]
}

/** The huddle endpoints the session calls. */
export type IHuddleApi = Pick<
  ChannelsApi,
  | 'startHuddle'
  | 'joinHuddle'
  | 'updateHuddleSeat'
  | 'leaveHuddle'
  | 'setHuddleArc'
  | 'judgeHuddleArcInsight'
  | 'addHuddleAudio'
  | 'addHuddleArcLine'
  | 'inviteToHuddle'
  | 'confirmHuddleSpeaker'
>

/** The parts of HuddleAudio the session drives. */
export type IHuddleAudio = Pick<
  HuddleAudio,
  | 'outgoingTrack'
  | 'resume'
  | 'replaceMic'
  | 'setOutputDevice'
  | 'setMuted'
  | 'addRemote'
  | 'removeRemote'
  | 'levels'
  | 'openRoomAudio'
  | 'openOwnVoice'
  | 'setArcVoice'
  | 'close'
>

/** The parts of HuddleMesh the session drives. */
export interface IHuddleMeshLike {
  syncPeers: (peers: IHuddle['peers']) => void
  handleSignal: (signal: IHuddleSignalDelivery) => Promise<void>
  close: () => void
}

export interface IHuddleSessionDeps {
  api: IHuddleApi
  getMicrophone: () => Promise<MediaStream>
  createAudioContext: () => AudioContext
  createAudio: (mic: MediaStream, context: AudioContext) => IHuddleAudio
  createMesh: (deps: IHuddleMeshDeps) => IHuddleMeshLike
  /** Records this browser's voice for the transcript; null where the browser can't record. */
  createRecorder?: (recorder: {
    voice: MediaStream
    getLevel: () => number
    onChunk: (chunk: IHuddleAudioChunk) => void
  }) => { stop: () => void } | null
  sendSignal: (signal: IHuddleSignal) => void
  onSignal: (listener: (signal: IHuddleSignalDelivery) => void) => () => void
  onHuddle: (listener: (huddle: IHuddle) => void) => () => void
  /** Arc's host says this tab's user just spoke to Arc; answered only if its mic agrees. */
  onSpeakerCheck?: (listener: (check: IHuddleSpeakerCheck) => void) => () => void
  /** The conversation's live huddle, for its button, as this tab learns of it. */
  publish: (channelId: string, huddle: IHuddle | null) => void
  notify: (message: string, isError: boolean) => void
  heartbeatMs?: number
}

/**
 * This tab in one huddle: the seat, the mic, a connection to every other seat,
 * and the heartbeat that keeps the seat. Ends when you leave, when the room ends,
 * or when your seat goes to another of your tabs.
 */
export class HuddleSession {
  private snapshot: IHuddleSnapshot

  private readonly listeners = new Set<() => void>()

  private audio: IHuddleAudio | null = null

  private mesh: IHuddleMeshLike | null = null

  private heartbeat: ReturnType<typeof setInterval> | null = null

  private unsubscribers: (() => void)[] = []

  private earlySignals: IHuddleSignalDelivery[] = []

  private recorder: { stop: () => void } | null = null

  private speakers: HuddleSpeakerTracker | null = null

  private hasWarnedTranscript = false

  constructor(
    private readonly deps: IHuddleSessionDeps,
    channelId: string,
  ) {
    this.snapshot = {
      status: HuddleSessionStatus.Connecting,
      channelId,
      huddle: null,
      peerId: null,
      isMuted: false,
      connectedPeerIds: [],
    }
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  readonly getSnapshot = (): IHuddleSnapshot => this.snapshot

  get levels(): Map<string, number> {
    return this.audio?.levels() ?? new Map()
  }

  /** Start the conversation's huddle (or join the one going), or join `huddleId`. Call in a click. */
  async open(huddleId?: string): Promise<void> {
    const { channelId } = this.snapshot
    let mic: MediaStream | null = null
    let context: AudioContext | null = null
    // Others may call this tab the moment the server seats it, before the join returns.
    this.unsubscribers.push(
      this.deps.onSignal((signal) => {
        if (this.mesh) void this.mesh.handleSignal(signal)
        else if (this.earlySignals.length < EARLY_SIGNAL_LIMIT) this.earlySignals.push(signal)
      }),
    )
    try {
      context = this.deps.createAudioContext()
      mic = await this.deps.getMicrophone()
      const joined = huddleId
        ? await this.deps.api.joinHuddle(channelId, huddleId)
        : await this.deps.api.startHuddle(channelId)
      if (this.snapshot.status === HuddleSessionStatus.Ended) {
        mic.getTracks().forEach((track) => track.stop())
        void context.close()
        void this.deps.api.leaveHuddle(channelId, joined.huddle.id, joined.peerId, false)
        return
      }
      this.connect(mic, context, joined)
      mic = null
      context = null
      await this.audio?.resume()
    } catch (err) {
      mic?.getTracks().forEach((track) => track.stop())
      void context?.close()
      this.deps.notify((err as Error)?.message || 'Couldn’t join the huddle.', true)
      this.finish()
    }
  }

  toggleMute(): void {
    this.setMuted(!this.snapshot.isMuted)
  }

  setMuted(isMuted: boolean): void {
    if (this.snapshot.status === HuddleSessionStatus.Ended || isMuted === this.snapshot.isMuted) {
      return
    }
    this.audio?.setMuted(isMuted)
    this.update({ isMuted })
    void this.beat()
  }

  /** Talk through the mic `getMicrophone` now picks, without leaving the room. */
  async switchMicrophone(): Promise<void> {
    if (this.snapshot.status !== HuddleSessionStatus.Live || !this.audio) return
    try {
      const mic = await this.deps.getMicrophone()
      if (this.audio) this.audio.replaceMic(mic)
      else mic.getTracks().forEach((track) => track.stop())
    } catch (err) {
      this.deps.notify((err as Error)?.message || 'Couldn’t switch microphones.', true)
    }
  }

  setOutputDevice(outputId: string | null): void {
    this.audio?.setOutputDevice(outputId)
  }

  /** Leave the room from this tab. */
  leave(): void {
    if (this.snapshot.status === HuddleSessionStatus.Ended) return
    const { channelId, huddle, peerId } = this.snapshot
    if (huddle && peerId) void this.deps.api.leaveHuddle(channelId, huddle.id, peerId, false)
    this.finish()
  }

  /** The tab is closing: give the seat up in a request that outlives the page. */
  handlePageHide(): void {
    const { channelId, huddle, peerId, status } = this.snapshot
    if (status === HuddleSessionStatus.Ended || !huddle || !peerId) return
    void this.deps.api.leaveHuddle(channelId, huddle.id, peerId, true)
  }

  /** The room as Arc should hear it. */
  openRoomAudio(): MediaStream {
    if (!this.audio) throw new Error('You’re not in a huddle.')
    return this.audio.openRoomAudio()
  }

  setArcVoice(stream: MediaStream | null): void {
    this.audio?.setArcVoice(stream)
  }

  /** Tell the room Arc is here (hosted from this tab), or gone. */
  async setArcIn(isArcIn: boolean): Promise<void> {
    const { channelId, huddle, peerId } = this.snapshot
    if (!huddle || !peerId || this.snapshot.status === HuddleSessionStatus.Ended) return
    const updated = await this.deps.api.setHuddleArc(channelId, huddle.id, peerId, isArcIn)
    if (updated) this.apply(updated)
  }

  /** Whether Arc, hosted from this tab, has something worth saying to the room unasked. */
  async judgeArcInsight(lines: string[], arcSaid: string[]): Promise<string | null> {
    const { channelId, huddle, peerId, status } = this.snapshot
    if (!huddle || !peerId || status !== HuddleSessionStatus.Live) return null
    const { instruction } = await this.deps.api.judgeHuddleArcInsight(
      channelId,
      huddle.id,
      peerId,
      lines,
      arcSaid,
    )
    return instruction
  }

  /** Ring these members of the conversation to join. */
  async invite(userIds: string[]): Promise<void> {
    const { channelId, huddle, peerId, status } = this.snapshot
    if (!huddle || !peerId || status !== HuddleSessionStatus.Live || !userIds.length) return
    await this.deps.api.inviteToHuddle(channelId, huddle.id, peerId, userIds)
  }

  /** Who said the line just captioned, by user id, judged over how long it took to say. */
  recentSpeakerUserId(windowMs?: number): string | null {
    const key = this.speakers?.loudest(windowMs)
    const { huddle, peerId } = this.snapshot
    if (!key || !huddle) return null
    const speakerPeerId = key === LOCAL_LEVEL_KEY ? peerId : key
    return huddle.peers.find((peer) => peer.peerId === speakerPeerId)?.userId ?? null
  }

  /** Something Arc, hosted from this tab, said: for the transcript. */
  addArcLine(text: string): void {
    const { channelId, huddle, peerId } = this.snapshot
    if (!huddle || !peerId || !text.trim()) return
    const spokenAt = new Date().toISOString()
    this.deps.api
      .addHuddleArcLine(channelId, huddle.id, peerId, text, spokenAt)
      .catch(() => this.warnTranscript())
  }

  private connect(mic: MediaStream, context: AudioContext, joined: IHuddleJoin): void {
    const { huddle, peerId } = joined
    const audio = this.deps.createAudio(mic, context)
    this.audio = audio
    this.mesh = this.createMesh(audio, joined)
    this.unsubscribers.push(
      this.deps.onHuddle((update) => {
        if (update.id === huddle.id) this.apply(update)
      }),
      this.deps.onSpeakerCheck?.((check) => this.answerSpeakerCheck(check)) ?? (() => {}),
    )
    this.recorder = this.startRecorder(audio, huddle.id, peerId)
    this.speakers = new HuddleSpeakerTracker(() => audio.levels())
    this.update({ status: HuddleSessionStatus.Live, peerId })
    this.apply(huddle)
    const early = this.earlySignals
    this.earlySignals = []
    early.forEach((signal) => void this.mesh?.handleSignal(signal))
    this.heartbeat = setInterval(
      () => void this.beat(),
      this.deps.heartbeatMs ?? HUDDLE_HEARTBEAT_MS,
    )
  }

  private createMesh(
    audio: IHuddleAudio,
    { huddle, peerId, iceServers }: IHuddleJoin,
  ): IHuddleMeshLike {
    return this.deps.createMesh({
      selfPeerId: peerId,
      huddleId: huddle.id,
      iceServers,
      outgoingTrack: audio.outgoingTrack,
      sendSignal: this.deps.sendSignal,
      onRemoteStream: (remotePeerId, stream) => {
        audio.addRemote(remotePeerId, stream)
      },
      onPeerGone: (remotePeerId) => {
        audio.removeRemote(remotePeerId)
        this.setConnected(remotePeerId, false)
      },
      onConnectionChange: (remotePeerId, state) => {
        this.setConnected(remotePeerId, state === 'connected')
      },
    })
  }

  private startRecorder(
    audio: IHuddleAudio,
    huddleId: string,
    peerId: string,
  ): { stop: () => void } | null {
    return (
      this.deps.createRecorder?.({
        voice: audio.openOwnVoice(),
        getLevel: () => audio.levels().get(LOCAL_LEVEL_KEY) ?? 0,
        onChunk: (chunk) => {
          this.deps.api
            .addHuddleAudio(this.snapshot.channelId, huddleId, peerId, chunk)
            .catch(() => this.warnTranscript())
        },
      }) ?? null
    )
  }

  private async beat(): Promise<void> {
    const { channelId, huddle, peerId, isMuted, status } = this.snapshot
    if (status !== HuddleSessionStatus.Live || !huddle || !peerId) return
    try {
      const result = await this.deps.api.updateHuddleSeat(channelId, huddle.id, peerId, isMuted)
      if (!result.isSeated) {
        this.deps.notify(
          'You left the huddle here: it carried on in another tab, or the line dropped.',
          false,
        )
        this.finish()
        return
      }
      if (result.huddle) this.apply(result.huddle)
    } catch {
      // A missed heartbeat is retried on the next; the seat outlasts a few.
    }
  }

  private apply(huddle: IHuddle): void {
    if (this.snapshot.status === HuddleSessionStatus.Ended) return
    this.deps.publish(huddle.channelId, huddle)
    const isSeated = huddle.peers.some(({ peerId }) => peerId === this.snapshot.peerId)
    if (huddle.status !== HuddleStatus.Live || !isSeated) {
      this.update({ huddle })
      this.finish()
      return
    }
    if (!isSameRoom(this.snapshot.huddle, huddle)) this.update({ huddle })
    // Every beat, changed or not: the mesh re-offers stalled links and drops stale ones here.
    this.mesh?.syncPeers(huddle.peers)
  }

  /** Vouch that this browser's own mic was talking, so a request runs as its user. Silent otherwise. */
  private answerSpeakerCheck({
    checkId,
    channelId,
    huddleId,
    fromAgoMs,
    toAgoMs,
  }: IHuddleSpeakerCheck): void {
    const { huddle, peerId } = this.snapshot
    if (!huddle || huddle.id !== huddleId || !peerId) return
    if (!this.speakers?.hasSpoken(LOCAL_LEVEL_KEY, fromAgoMs, toAgoMs)) return
    this.deps.api.confirmHuddleSpeaker(channelId, huddleId, checkId, peerId).catch(() => {
      // Unconfirmed, the request runs as Arc's host; nothing to tell this person.
    })
  }

  /** Once per huddle: a gap in the transcript is worth knowing about, not worth repeating. */
  private warnTranscript(): void {
    if (this.hasWarnedTranscript) return
    this.hasWarnedTranscript = true
    this.deps.notify('Part of this huddle couldn’t be added to its transcript.', false)
  }

  private setConnected(peerId: string, isConnected: boolean): void {
    if (this.snapshot.connectedPeerIds.includes(peerId) === isConnected) return
    const others = this.snapshot.connectedPeerIds.filter((id) => id !== peerId)
    this.update({ connectedPeerIds: isConnected ? [...others, peerId] : others })
  }

  private finish(): void {
    if (this.heartbeat) clearInterval(this.heartbeat)
    this.heartbeat = null
    this.unsubscribers.forEach((unsubscribe) => unsubscribe())
    this.unsubscribers = []
    this.earlySignals = []
    this.recorder?.stop()
    this.recorder = null
    this.speakers?.stop()
    this.speakers = null
    this.mesh?.close()
    this.mesh = null
    this.audio?.close()
    this.audio = null
    this.update({ status: HuddleSessionStatus.Ended, connectedPeerIds: [] })
  }

  private update(patch: Partial<IHuddleSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch }
    this.listeners.forEach((listener) => listener())
  }
}
